import React, { useState } from "react";
import DateRangePickerComponent from "./CustomDateRangePicker";
import apiClient from "../utils/apiClient";
import moment from "moment";
import "bootstrap/dist/css/bootstrap.min.css";
import { Link } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Select from "react-select";

const ReceiptPendingReturnableReport = () => {
  const [dateRange, setDateRange] = useState([null, null]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openRow, setOpenRow] = useState(null);
  const [selectedLedger, setSelectedLedger] = useState("");
  const ledgerOptions = [
    { value: "ALL", label: "All Ledgers" },
    ...Array.from(new Set(tableData.map((r) => r.partyName))).map((l) => ({
      value: l,
      label: l,
    })),
  ];

  const exportToExcel = async () => {
    if (!tableData || tableData.length === 0) {
      alert("No data to export");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheetName = "Pending Returnables";
    const sheet = workbook.addWorksheet(sheetName);

    sheet.columns = [
      { header: "S.No", width: 8 },
      { header: "Ledger", width: 40 },
      { header: "Item", width: 25 },
      { header: "Received", width: 12 }, // 🔁 changed
      { header: "Sent", width: 12 }, // 🔁 changed
      { header: "Pending", width: 12 },
    ];

    sheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "center" };
    });

    /* ============================
         DATA PREP
      ============================ */

    const excelData =
      selectedLedger && selectedLedger.value !== "ALL"
        ? tableData.filter((r) => r.partyName === selectedLedger.value)
        : tableData;

    const grouped = excelData.reduce((acc, r) => {
      if (!acc[r.partyName]) acc[r.partyName] = [];
      acc[r.partyName].push(r);
      return acc;
    }, {});

    let sno = 0;
    const receivedDetailRows = [];
    const receivedRowLinkMap = {}; // sno -> first received detail row

    /* ============================
         SUMMARY ROWS
      ============================ */

    Object.entries(grouped).forEach(([ledger, rows]) => {
      rows.forEach((row, idx) => {
        sno++;

        sheet.addRow([
          sno,
          idx === 0 ? ledger : "", // merged ledger
          row.itemName,
          row.totalReturned, // RECEIVED
          row.totalIssued, // SENT
          row.pending,
        ]);

        // Collect RECEIVED details
        row.returnedDetails?.forEach((d) => {
          receivedDetailRows.push({
            sno,
            date: moment(d.date).format("DD-MM-YYYY"),
            voucher: d.voucherNo,
            qty: d.returnedQty,
          });
        });
      });
    });

    const summaryEndRow = sheet.lastRow.number;

    /* ============================
         RECEIVED DETAILS SECTION
      ============================ */

    if (receivedDetailRows.length > 0) {
      sheet.addRow([]);
      sheet.addRow([]);

      const titleRowNo = sheet.lastRow.number + 1;

      const titleRow = sheet.addRow(["Received Details"]);
      titleRow.font = { bold: true };
      sheet.mergeCells(`A${titleRowNo}:D${titleRowNo}`);

      const header = sheet.addRow([
        "S.No",
        "Received Date",
        "Voucher No",
        "Received Qty",
      ]);

      header.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center" };
      });

      receivedDetailRows.forEach((d) => {
        const r = sheet.addRow([d.sno, d.date, d.voucher, d.qty]);

        // Save FIRST received row for hyperlink
        if (!receivedRowLinkMap[d.sno]) {
          receivedRowLinkMap[d.sno] = r.number;
        }
      });
    }

    /* ============================
         HYPERLINKS
      ============================ */

    for (let r = 2; r <= summaryEndRow; r++) {
      const row = sheet.getRow(r);

      const snoCell = row.getCell(1);
      const receivedCell = row.getCell(4); // 🔁 Received column
      const snoVal = snoCell.value;

      if (receivedRowLinkMap[snoVal]) {
        const targetRow = receivedRowLinkMap[snoVal];
        const link = `#'${sheetName}'!A${targetRow}`;

        snoCell.value = {
          text: snoVal.toString(),
          hyperlink: link,
        };
        snoCell.font = { color: { argb: "FF0000FF" }, underline: true };

        receivedCell.value = {
          text: receivedCell.value.toString(),
          hyperlink: link,
        };
        receivedCell.font = { color: { argb: "FF0000FF" }, underline: true };
      }
    }

    /* ============================
         EXPORT
      ============================ */

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `Pending_Returnables_${moment().format("DD-MM-YYYY")}.xlsx`);
  };

  // 🔥 Fetch data from API
  const fetchPendingReturnable = async () => {
    try {
      setLoading(true);
      setError("");

      const payload = {
        start: moment(dateRange[0]).format("YYYY-MM-DD"),
        end: moment(dateRange[1]).format("YYYY-MM-DD"),
      };

      const res = await apiClient.post("/getPendingIssuedFromReturn", payload);

      setTableData(res.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };
  const filteredData =
    selectedLedger && selectedLedger.value !== "ALL"
      ? tableData.filter((r) => r.partyName === selectedLedger.value)
      : tableData;

  const grouped = filteredData.reduce((acc, row) => {
    if (!acc[row.partyName]) acc[row.partyName] = [];
    acc[row.partyName].push(row);
    return acc;
  }, {});

  let serialNo = 0;

  return (
    <div className="formcontainer mt-3">
      <h5 className="mb-3">Pending Returnables</h5>

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-3">
          <label className="form-label">Date Range</label>
          <DateRangePickerComponent value={dateRange} onChange={setDateRange} />
        </div>
        {tableData?.length > 0 && (
          <div className="col-md-3">
            <label className="form-label">Ledger</label>
            <Select
              options={ledgerOptions}
              value={selectedLedger}
              onChange={setSelectedLedger}
              isClearable
              placeholder="Select Ledger"
              classNamePrefix="react-select"
            />
          </div>
        )}

        {/* GET REPORT BUTTON */}
        <div className="col-md-2 d-flex align-items-end">
          <button
            className="btn btn-outline-success w-100"
            onClick={fetchPendingReturnable}
          >
            Get Report
          </button>
        </div>

        <div className="col-md-2 d-flex align-items-end">
          <button className="btn btn-outline-secondary" onClick={exportToExcel}>
            Export Excel
          </button>
        </div>
      </div>

      {/* Loading / Error */}
      {loading && <div className="alert alert-info">Loading...</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Table */}
      <div className="table-responsive mt-3">
        <table className="table table-bordered table-striped">
          <thead>
            <tr>
              <th>#</th>
              <th>Ledger</th>
              <th>Item</th>
              <th>Received</th>
              <th>Sent</th>
              <th>Pending</th>
              <th>Details</th>
            </tr>
          </thead>

          <tbody>
            {Object.keys(grouped).length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  No Data Found
                </td>
              </tr>
            ) : (
              Object.entries(grouped).map(([ledger, rows]) =>
                rows.map((row) => {
                  serialNo++;

                  return (
                    <React.Fragment key={row.itemId}>
                      {/* MAIN ROW */}
                      <tr>
                        <td className="fw-bold text-center">{serialNo}</td>
                        <td>{ledger}</td>
                        <td>{row.itemName}</td>
                        <td className="fw-bold">{row.totalReturned}</td>
                        <td className="fw-bold">{row.totalIssued}</td>
                        <td className="fw-bold text-danger">{row.pending}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() =>
                              setOpenRow(
                                openRow === row.itemId ? null : row.itemId
                              )
                            }
                          >
                            {openRow === row.itemId ? "Hide" : "Details"}
                          </button>
                        </td>
                      </tr>

                      {/* DETAILS ROW */}
                      {openRow == row.itemId && (
                        <tr>
                          <td></td>
                          <td></td>
                          <td colSpan="5">
                            <div className="p-2 border rounded bg-light">
                              {row.issuedDetails?.length > 0 && (
                                <>
                                  <h6>Sent Details</h6>

                                  <table className="table table-sm table-bordered mb-3">
                                    <thead>
                                      <tr>
                                        <th>Date</th>
                                        <th>Challan No</th>
                                        <th>Issued Qty</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {row.issuedDetails.map((d, i) => (
                                        <tr key={i}>
                                          <td>
                                            {moment(d.date).format(
                                              "DD-MM-YYYY"
                                            )}
                                          </td>
                                          <td>
                                            <Link
                                              to={`/previewDeliveryChallan?challanId=${d.id}&type=challan`}
                                              target="_blank"
                                            >
                                              {d.challanNo}
                                            </Link>
                                          </td>
                                          <td>{d.issuedQty}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </>
                              )}

                              {row.returnedDetails?.length > 0 && (
                                <>
                                  <h6>Received Details</h6>
                                  <table className="table table-sm table-bordered">
                                    <thead>
                                      <tr>
                                        <th>Date</th>
                                        <th>Voucher No</th>
                                        <th>Returned Qty</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {row.returnedDetails.map((d, i) => (
                                        <tr key={i}>
                                          <td>
                                            {moment(d.date).format(
                                              "DD-MM-YYYY"
                                            )}
                                          </td>
                                          <td>
                                            <Link
                                              to={`/PreviewExternalReceiptVoucher?voucherId=${d.id}&type=external`}
                                              target="_blank"
                                            >
                                              {d.voucherNo}
                                            </Link>
                                          </td>
                                          <td>{d.returnedQty}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReceiptPendingReturnableReport;
